import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import zoomSdk from "@zoom/appssdk";
import { ThemeProvider, Container, Tabs, Tab, Box, CssBaseline, Typography } from '@mui/material';
import { OnboardingView } from './components/Onboarding';
import Participants from './components/Participants';
import CallLogs from '@repo/shared/components/CallLogs';
import {createLightTheme, createDarkTheme} from '@repo/shared/components/theme';
import useDocumentDirection from '@repo/shared/hooks/useDocumentDirection';
import RtlCacheProvider from '@repo/shared/i18n/RtlCacheProvider';
import { useMediaQuery } from '@mui/material';
import { maybeAddZoomAccountId, openURL, handleZoomUnauthorized } from './utils/utils';
import IdentityBar from '@repo/shared/components/IdentityBar';
import IntercomSupport from '@repo/shared/components/IntercomSupport';
import WiringBanner from './components/WiringBanner';
import useLocalStorage from '../../../packages/shared/src/hooks/useLocalStorage';
import Meetings from './components/Meetings';
import { Toaster, toast } from 'sonner';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import { PolyAuthProvider, usePolyAuth } from '@repo/shared/hooks/PolyAuthContext';
import { SWRProvider } from '@repo/shared/hooks/swrConfig';

const clientSideID = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID;
const offlineView = "offline";


// Main App component with Router wrapper
// Provides Router context for components that use useLocation, useNavigate, etc.
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

// Wrapper to access usePolyAuth() inside PolyAuthProvider for account filtering
function ZoomIdentityBar({ onLogin, themeString }) {
  const { zoomEnabledAccounts } = usePolyAuth();
  const accounts = zoomEnabledAccounts
    .filter(a => a.has_zoom_app)
    .map(a => ({ id: a.account_id, name: a.name }));
  return <IdentityBar onLogin={onLogin} themeString={themeString} accounts={accounts} />;
}

// Triggers silent Zoom SDK reauth when the backend signals stale scopes.
// Must live inside PolyAuthProvider (to read context) and after zoomSdk.config().
function ZoomReauthHandler() {
  const { needsZoomReauth, setNeedsZoomReauth, appContext } = usePolyAuth();
  useEffect(() => {
    if (!needsZoomReauth || !appContext) return;
    console.log('Zoom token needs scope upgrade, triggering silent reauth');
    handleZoomUnauthorized(appContext).then(() => {
      setNeedsZoomReauth(false);
      toast.success('Zoom token has been refreshed, please try again');
    }).catch((err) => {
      console.error('Silent Zoom reauth failed:', err);
    });
  }, [needsZoomReauth, appContext]);
  return null;
}

// Internal AppContent component that handles the actual app logic
function AppContent() {
  const { t } = useTranslation('zoom');
  const [meetingUUID, setMeetingUUID] = useState();
  const [zoomAccountId, setZoomAccountId] = useLocalStorage('ZoomAccountId', 1000000000);
  const [appContext, setAppContext] = useState(null);
  const [configState, setConfigState] = useState([]);
  const [runningContext, setRunningContext] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(() => {
    const storedValue = localStorage.getItem('isPhoneAppOnboarded');
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const [verificationJWT] = useLocalStorage('verificationJWT');
  const ldClient = useLDClient();
  const themeString = useMediaQuery('(prefers-color-scheme: dark)') ? "dark" : "light";
  const direction = useDocumentDirection();
  const theme = useMemo(
    () => themeString === "dark" ? createDarkTheme(direction) : createLightTheme(direction),
    [themeString, direction]
  );
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const newAccountId = urlParams.get('accountId');
    if (newAccountId && newAccountId !== zoomAccountId) {
      setZoomAccountId(newAccountId, 1000000000);
      console.log('Extracted accountId from URL:', newAccountId);
    }
  }, [window.location.search]);

  useEffect(() => {
    if (!ldClient) return;
    let context = { anonymous: true };
    if (verificationJWT) {
      try {
        const jwtData = typeof verificationJWT === 'string' ? JSON.parse(verificationJWT) : verificationJWT;
        const key = jwtData?.sub || jwtData?.subject || jwtData?.verification?.subject;
        if (key) context = { key: String(key), kind: 'user' };
      } catch {}
    }
    ldClient.identify(context);
  }, [verificationJWT, ldClient]);

  useEffect(() => {
    async function zoomConfig() {
      console.log("Calling zoomsdk config...");
      try {
        const receivedConfigState = await zoomSdk.config({
          popoutSize: { width: 480, height: 720 },
          capabilities: [
            "getSupportedJsApis",
            "authorize",
            "onAuthorized",
            "onRunningContextChange",
            "onRunningContext",
            "onMeetingContext",
          ]
        });

        const fetchedAppContext = await zoomSdk.getAppContext();
        const fetchedRunningContext = await zoomSdk.getRunningContext();

        if (fetchedRunningContext.context === "inMeeting") {
          const meetingUUIDResponse = await zoomSdk.getMeetingUUID();
          if (meetingUUIDResponse?.meetingUUID) {
            setMeetingUUID(meetingUUIDResponse?.meetingUUID);
          }
        }

        setRunningContext(fetchedRunningContext.context);
        zoomSdk.onRunningContextChange( ({ runningContext, timestamp }) => {
          console.log("onRunningContextChange called", runningContext.context);
          setRunningContext(runningContext.context);
          if (runningContext.context === "inMeeting") {
            async function getMeetingUUID() {
              const meetingUUIDResponse = await zoomSdk.getMeetingUUID();
              if (meetingUUIDResponse?.meetingUUID) {
                setMeetingUUID(meetingUUIDResponse?.meetingUUID);
              }
            }
            getMeetingUUID();
          }
        });
        setAppContext(fetchedAppContext.context);
        setConfigState(receivedConfigState);

      } catch (error) {
        // TODO: JMC - this happens when the internet is down for the user,
        // we should handle this gracefully
        console.error("Error in zoomConfig: ", error);
        setRunningContext(offlineView);
      }
    }

    if (!runningContext) {
      zoomConfig();
    }
  }, [configState, runningContext]);

  const handleLogin = async (sessionToken) => {
    if (zoomAccountId && appContext) {
      await maybeAddZoomAccountId(sessionToken, zoomAccountId, appContext);
    }
  };

  return (<>
    <RtlCacheProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PolyAuthProvider
        appContext={appContext}
        meetingUUID={meetingUUID}
        requireAccount={runningContext === 'inMainClient'}
      >
        <ZoomReauthHandler />
        <SWRProvider>
        {/* Consider setting this from LaunchDarkly flag */}
        <Container maxWidth="lg" sx={{ py: 1, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <IntercomSupport />
          <WiringBanner />
          {(runningContext == offlineView) && <><Typography variant="h6">{t('shared:common.offline')}</Typography></>}
          {(runningContext == "inMainClient") && (
            <>
              <ZoomIdentityBar onLogin={handleLogin} themeString={themeString} />
              {!isOnboarded ? (
                <OnboardingView setIsOnboarded={setIsOnboarded} />
              ) : (
                <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, flexShrink: 0 }}>
                    <Tabs
                      value={(() => {
                        if (window.location.pathname.startsWith('/logs')) return 1;
                        return 0;
                      })()}
                      onChange={(e, newValue) => {
                        const tabRoutes = ['/meetings', '/logs/log'];
                        navigate(tabRoutes[newValue] + (zoomAccountId ? `?accountId=${zoomAccountId}` : null));
                      }}
                      aria-label="management tabs"
                    >
                      <Tab label={t('tabs.meetings')} />
                      <Tab label={t('tabs.logs')} />
                    </Tabs>
                  </Box>
                  <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    {/* Router-based tab rendering */}
                    <Routes>
                      <Route path="/meetings" element={<Meetings />} />
                      <Route path="/logs/log/:logId" element={<CallLogs basePath="/logs" openURL={openURL} defaultViewMode="user" />} />
                      <Route path="/logs/log" element={<CallLogs basePath="/logs" openURL={openURL} defaultViewMode="user" />} />
                      <Route path="/logs" element={<Navigate to="/logs/log" replace />} />
                      <Route path="*" element={<Navigate to={`/meetings${zoomAccountId ? `?accountId=${zoomAccountId}` : ''}`} replace />} />
                    </Routes>
                  </Box>
                </Box>
              )}
            </>
          )}
          {runningContext == "inMeeting" && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {/* TODO: Parse the returned JSON and display the participants */}
              <Participants />  
            </Box>
          )}
          <Toaster position="top-center" richColors duration={5000} />
        </Container>
        </SWRProvider>
      </PolyAuthProvider>
    </ThemeProvider>
    </RtlCacheProvider>
  </>);
}

export default withLDProvider({
  clientSideID,
  deferInitialization: true,
  reactOptions: { useCamelCaseFlagKeys: false },
})(App);